import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useStreamStore } from "@/lib/streamStore";
import type { TraceEvent } from "@/lib/streamStore";
import { useAppStore } from "@/lib/store";
import { QueryClient } from "@tanstack/react-query";

// Map of sessionId -> AbortController for active streams
const controllersBySession = new Map<string, AbortController>();

// Global query client reference (will be set by the hook)
let globalQueryClient: QueryClient | null = null;

export function setQueryClient(queryClient: QueryClient) {
  globalQueryClient = queryClient;
}

export interface StartStreamOptions {
  sessionId: string;
  text: string;
  skillCommand?: string;
  model?: string;
  webSearchEnabled?: boolean;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export async function startStream({
  sessionId,
  text,
  skillCommand,
  model,
  webSearchEnabled,
  onComplete,
  onError,
}: StartStreamOptions): Promise<void> {
  const store = useStreamStore.getState();
  const { apiUrl, apiToken } = useAppStore.getState();

  if (!sessionId || !apiToken) {
    const error = new Error("Session ID or API token not set");
    onError?.(error);
    throw error;
  }

  // Cancel any existing stream for this session only
  const existingController = controllersBySession.get(sessionId);
  if (existingController) {
    console.log(`Cancelling existing stream for session ${sessionId}`);
    existingController.abort();
    controllersBySession.delete(sessionId);
  }

  const controller = new AbortController();
  controllersBySession.set(sessionId, controller);

  // Initialize stream state
  store.setStreaming(sessionId, true);
  store.setPendingUserMessage(sessionId, text);
  store.setError(sessionId, null);

  // Ensure apiUrl doesn't have trailing slash
  const baseUrl = apiUrl.replace(/\/$/, "");
  const url = `${baseUrl}/api/v1/sessions/${sessionId}/messages/stream`;

  // Only include skill_command if explicitly provided
  const bodyData: { text: string; skill_command?: string; stream: boolean; model?: string; web_search_enabled?: boolean } = {
    text,
    stream: true,
  };

  if (skillCommand && skillCommand.trim()) {
    bodyData.skill_command = skillCommand.trim();
  }

  if (model && model.trim()) {
    bodyData.model = model.trim();
  }

  if (webSearchEnabled !== undefined) {
    bodyData.web_search_enabled = webSearchEnabled;
  }

  const body = JSON.stringify(bodyData);

  try {
    await fetchEventSource(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body,
      signal: controller.signal,
      openWhenHidden: true,
      async onopen(res) {
        if (!res.ok) {
          const errorText = await res.text().catch(() => res.statusText);
          throw new Error(`HTTP ${res.status}: ${errorText}`);
        }
      },
      onmessage(event) {
        // Only process events if this controller is still active
        if (controller.signal.aborted) {
          return;
        }

        if (event.event === "session_started") {
          // Session started, ready for events
        } else if (event.event === "trace") {
          try {
            const traceData = JSON.parse(event.data) as TraceEvent;
            store.appendTrace(sessionId, traceData);
          } catch (e) {
            console.error("Failed to parse trace event:", e);
          }
        } else if (event.event === "final") {
          try {
            const finalData = JSON.parse(event.data);
            store.setFinalText(sessionId, finalData.text || "");
            store.setPendingUserMessage(sessionId, null);

            // Invalidate queries for this session only
            if (globalQueryClient) {
              globalQueryClient.invalidateQueries({
                queryKey: ["worklog", sessionId],
              });
              globalQueryClient.invalidateQueries({
                queryKey: ["artifacts", sessionId],
              });
            }

            controllersBySession.delete(sessionId);
            onComplete?.();
          } catch (e) {
            console.error("Failed to parse final event:", e);
            store.setError(sessionId, "Failed to parse response");
            store.setStreaming(sessionId, false);
            store.setPendingUserMessage(sessionId, null);
            controllersBySession.delete(sessionId);
          }
        } else if (event.event === "error") {
          try {
            const errorData = JSON.parse(event.data);
            store.setError(sessionId, errorData.message || "An error occurred");
            store.setStreaming(sessionId, false);
            store.setPendingUserMessage(sessionId, null);
            controllersBySession.delete(sessionId);
          } catch {
            store.setError(sessionId, "An error occurred");
            store.setStreaming(sessionId, false);
            store.setPendingUserMessage(sessionId, null);
            controllersBySession.delete(sessionId);
          }
        }
      },
      onerror(err) {
        if (controller.signal.aborted) {
          return; // Ignore abort errors
        }
        console.error("Stream error", { sessionId, error: err });
        store.setError(
          sessionId,
          err.message || "Streaming error occurred"
        );
        store.setStreaming(sessionId, false);
        store.setPendingUserMessage(sessionId, null);
        controllersBySession.delete(sessionId);
        onError?.(err);
        // Don't throw here - let fetchEventSource handle retries
        return;
      },
    });
  } catch (err) {
    if (controller.signal.aborted) {
      return; // Ignore abort errors
    }
    console.error("Failed to start stream", { sessionId, error: err });
    const error = err instanceof Error ? err : new Error("Unknown error");
    store.setError(sessionId, error.message);
    store.setStreaming(sessionId, false);
    store.setPendingUserMessage(sessionId, null);
    controllersBySession.delete(sessionId);
    onError?.(error);
    throw error;
  }
}

export function stopStream(sessionId: string): void {
  const controller = controllersBySession.get(sessionId);
  if (controller) {
    console.log(`Stopping stream for session ${sessionId}`);
    controller.abort();
    controllersBySession.delete(sessionId);
    const store = useStreamStore.getState();
    store.setStreaming(sessionId, false);
    store.setPendingUserMessage(sessionId, null);
  }
}

export function isStreaming(sessionId: string): boolean {
  return controllersBySession.has(sessionId);
}

